/**
 * /payroll-l3/fringe-funds — Fringe fund setup.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import { api } from "@/lib/api";
import { HelpHint } from "@/components/HelpHint";

export const Route = createFileRoute("/_auth/payroll-l3/fringe-funds")({
  component: FringeFundsPage,
});

interface FringeType {
  id: string;
  code: string;
  name: string;
  is_employer_paid: boolean;
}

interface FringeFund {
  id: string;
  entity_id: string;
  code: string | null;
  name: string;
  fringe_type_id: string;
  party_id: string | null;
  contact_name: string | null;
  contact_email: string | null;
  remittance_frequency: string | null;
  edi_format: string | null;
  notes: string | null;
}

interface PartyLite {
  id: string;
  name: string;
  party_type: string;
}

const FREQUENCIES = ["weekly", "biweekly", "monthly", "quarterly"];
const EDI_FORMATS = ["csv", "iuoe_pension", "ibew_pension", "custom_pdf"];

interface FundForm {
  code: string;
  name: string;
  fringe_type_id: string;
  party_id: string;
  contact_name: string;
  contact_email: string;
  remittance_frequency: string;
  edi_format: string;
  notes: string;
}

const blank = (): FundForm => ({
  code: "",
  name: "",
  fringe_type_id: "",
  party_id: "",
  contact_name: "",
  contact_email: "",
  remittance_frequency: "",
  edi_format: "",
  notes: "",
});

function FringeFundsPage() {
  const qc = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<FringeFund | null>(null);
  const [form, setForm] = useState<FundForm>(blank());
  const [error, setError] = useState<string | null>(null);

  const fundsQ = useQuery({
    queryKey: ["payroll-l3", "fringe-funds"],
    queryFn: () => api<FringeFund[]>("/api/payroll-l3/fringe-funds"),
  });

  const typesQ = useQuery({
    queryKey: ["payroll-l3", "fringe-types"],
    queryFn: () => api<FringeType[]>("/api/payroll-l3/fringe-types"),
  });

  // Parties endpoint — pulled best-effort; if shape differs, fall back gracefully.
  const partiesQ = useQuery({
    queryKey: ["parties", "vendors"],
    queryFn: async () => {
      try {
        const res = await api<PartyLite[] | { items?: PartyLite[] }>(
          "/api/parties?party_type=vendor&limit=500",
        );
        return Array.isArray(res) ? res : (res.items ?? []);
      } catch {
        return [] as PartyLite[];
      }
    },
  });

  const openCreate = () => {
    setEditing(null);
    setForm(blank());
    setError(null);
    setDialogOpen(true);
  };

  const openEdit = (f: FringeFund) => {
    setEditing(f);
    setForm({
      code: f.code ?? "",
      name: f.name,
      fringe_type_id: f.fringe_type_id,
      party_id: f.party_id ?? "",
      contact_name: f.contact_name ?? "",
      contact_email: f.contact_email ?? "",
      remittance_frequency: f.remittance_frequency ?? "",
      edi_format: f.edi_format ?? "",
      notes: f.notes ?? "",
    });
    setError(null);
    setDialogOpen(true);
  };

  const saveMut = useMutation({
    mutationFn: async () => {
      const body = {
        code: form.code || null,
        name: form.name,
        fringe_type_id: form.fringe_type_id,
        party_id: form.party_id || null,
        contact_name: form.contact_name || null,
        contact_email: form.contact_email || null,
        remittance_frequency: form.remittance_frequency || null,
        edi_format: form.edi_format || null,
        notes: form.notes || null,
      };
      if (editing) {
        return api(`/api/payroll-l3/fringe-funds/${editing.id}`, {
          method: "PATCH",
          body,
        });
      }
      // Create requires entity_id; backend assigns from CurrentEntityId, so we
      // need to read it. CBACreate forces it in the body, but FringeFundCreate
      // sets it server-side from CurrentEntityId — so we must still pass it.
      // Pull it from /api/auth/me/entities.
      const ents = await api<{ id: string; is_current: boolean }[]>(
        "/api/auth/me/entities",
      );
      const eid = ents.find((e) => e.is_current)?.id;
      if (!eid) throw new Error("Current entity not loaded");
      return api("/api/payroll-l3/fringe-funds", {
        method: "POST",
        body: { ...body, entity_id: eid },
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["payroll-l3", "fringe-funds"] });
      setDialogOpen(false);
      setForm(blank());
    },
    onError: (e: unknown) => setError(e instanceof Error ? e.message : "Failed to save"),
  });

  const typeById = (id: string) => typesQ.data?.find((t) => t.id === id);
  const partyById = (id: string) => partiesQ.data?.find((p) => p.id === id);

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" fontWeight={600}>Fringe Funds</Typography>
        <Stack direction="row" alignItems="center" gap={2}>
          <HelpHint docPath="payroll-l3/fringe-benefits.md" label="Fringe fund setup help" />
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            New Fund
          </Button>
        </Stack>
      </Stack>
      <Typography variant="body2" color="text.secondary" mb={1}>
        Benefit funds (H&W, pension, training, vacation) that receive employer
        contributions and dues check-off.
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        <strong>Fringe funds vs fringe rates:</strong> Fringe <em>rates</em> (dollar amounts per hour)
        are extracted automatically when you upload and confirm a CBA contract — find them under{" "}
        <strong>CBAs → View → Show rates</strong>. Fringe <em>funds</em> are the remittance
        recipients (e.g. "Southwest Carpenters Trust Fund") and must be set up once here so you
        can generate remittance reports to the correct payee.
      </Alert>

      {fundsQ.isLoading && <CircularProgress size={24} />}
      {fundsQ.error && <Alert severity="error">Failed to load fringe funds.</Alert>}

      {fundsQ.data && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Vendor</TableCell>
              <TableCell>Frequency</TableCell>
              <TableCell>Format</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {fundsQ.data.map((f) => (
              <TableRow key={f.id} hover>
                <TableCell>
                  <Stack spacing={0.5}>
                    <Stack direction="row" alignItems="center" gap={1}>
                      <span style={{ fontWeight: 600 }}>{f.name}</span>
                      {f.notes?.startsWith("Auto-created") && (
                        <Tooltip title="Auto-created from CBA — update the fund name, vendor, and contact">
                          <Chip
                            size="small"
                            icon={<WarningAmberIcon />}
                            label="Needs setup"
                            color="warning"
                            variant="outlined"
                          />
                        </Tooltip>
                      )}
                    </Stack>
                    {f.code && (
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
                        {f.code}
                      </Typography>
                    )}
                  </Stack>
                </TableCell>
                <TableCell>{typeById(f.fringe_type_id)?.name ?? "—"}</TableCell>
                <TableCell>
                  {f.party_id ? partyById(f.party_id)?.name ?? f.party_id : "—"}
                </TableCell>
                <TableCell>{f.remittance_frequency ?? "—"}</TableCell>
                <TableCell>{f.edi_format ?? "—"}</TableCell>
                <TableCell>
                  <Button size="small" startIcon={<EditIcon />} onClick={() => openEdit(f)}>
                    Edit
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {fundsQ.data.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ color: "text.secondary", py: 4 }}>
                  No fringe funds yet — add one for each H&W, pension, training, or vacation fund.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? "Edit Fringe Fund" : "New Fringe Fund"}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Name"
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              required
            />
            <TextField
              label="Code (optional)"
              value={form.code}
              onChange={(e) => setForm((p) => ({ ...p, code: e.target.value }))}
            />
            <TextField
              select
              label="Fringe Type"
              value={form.fringe_type_id}
              onChange={(e) => setForm((p) => ({ ...p, fringe_type_id: e.target.value }))}
              required
              disabled={!!editing}
              helperText={editing ? "Fringe type is locked after creation" : undefined}
            >
              {(typesQ.data ?? []).map((t) => (
                <MenuItem key={t.id} value={t.id}>{t.name} ({t.code})</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Vendor (party)"
              value={form.party_id}
              onChange={(e) => setForm((p) => ({ ...p, party_id: e.target.value }))}
              helperText="Linked AP vendor that receives remittance"
            >
              <MenuItem value="">—</MenuItem>
              {(partiesQ.data ?? []).map((p) => (
                <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
              ))}
            </TextField>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Contact Name"
                value={form.contact_name}
                onChange={(e) => setForm((p) => ({ ...p, contact_name: e.target.value }))}
                fullWidth
              />
              <TextField
                label="Contact Email"
                type="email"
                value={form.contact_email}
                onChange={(e) => setForm((p) => ({ ...p, contact_email: e.target.value }))}
                fullWidth
              />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField
                select
                label="Remittance Frequency"
                value={form.remittance_frequency}
                onChange={(e) => setForm((p) => ({ ...p, remittance_frequency: e.target.value }))}
                fullWidth
              >
                <MenuItem value="">—</MenuItem>
                {FREQUENCIES.map((f) => (
                  <MenuItem key={f} value={f}>{f}</MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Report Format"
                value={form.edi_format}
                onChange={(e) => setForm((p) => ({ ...p, edi_format: e.target.value }))}
                fullWidth
              >
                <MenuItem value="">—</MenuItem>
                {EDI_FORMATS.map((f) => (
                  <MenuItem key={f} value={f}>{f}</MenuItem>
                ))}
              </TextField>
            </Stack>
            <TextField
              label="Notes"
              value={form.notes}
              onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
              multiline
              minRows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!form.name || !form.fringe_type_id || saveMut.isPending}
            onClick={() => saveMut.mutate()}
          >
            {editing ? "Save" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
